<template>
  <div class="card history-card" :class="cardClass">
    <div class="history-card-header">
      <div>
        <div class="history-card-title text-truncate">{{ referralName }}</div>
        <span>{{ data.referralContactPhone }}</span>
      </div>
      <badge v-if="data.status" :class="statusClass" :value="data.status | statusName(statuses)" />
    </div>

    <p v-if="data.note" class="history-text">{{ data.note }}</p>
    <p v-if="data.commentText" class="history-comment">{{ data.commentText }}</p>

    <div class="info" v-if="data.whatsNext">
      <strong>What's next?</strong>
      <p class="info-text">{{ data.whatsNext }}</p>
    </div>

    <div class="history-card-footer">
      <div>
        <div>Added on {{ data.dateCreated | dateFormat('M/DD/YYYY') }}</div>
        <div v-if="data.linkSharedDate">Link shared on {{ data.linkSharedDate | dateFormat('M/DD/YYYY') }}</div>
        <div v-if="data.linkOpenedDate">Link opened on {{ data.linkOpenedDate | dateFormat('M/DD/YYYY') }}</div>
        <div v-if="data.vehiclePurchasedDate">Vehicle purchased on {{ data.vehiclePurchasedDate |
          dateFormat('M/DD/YYYY') }}</div>
      </div>

      <div class="earn-label" :class="earnLabelClass">{{ earnText }}</div>
    </div>

    <div class="history-card-actions" :class="{ stacked: data.status === ReferralProgramHistoryStatus.Purchased }">
      <template
        v-if="data.status === ReferralProgramHistoryStatus.Added || data.status === ReferralProgramHistoryStatus.Sent">
        <button class="mui-button sm stroked ripple" :style="strokedBtnStyles" @click.stop="onEdit()">Edit</button>
        <button class="mui-button sm ripple" :style="flatBtnStyles" @click.stop="onSend()">Share Referral Link</button>
      </template>
      <template v-if="data.status === ReferralProgramHistoryStatus.Engaged">
        <button class="mui-button sm stroked ripple" :style="strokedBtnStyles" @click.stop="onEdit()">Edit</button>
        <button class="mui-button sm ripple" :style="flatBtnStyles" @click.stop="onResend()">Resend Link</button>
      </template>
      <template v-if="data.status === ReferralProgramHistoryStatus.Purchased">
        <button class="mui-button sm stroked ripple" :style="strokedBtnStyles" @click.stop="onSendSalespersonSms()">Text {{ salesPersonName }}</button>
        <button class="mui-button sm ripple" :style="flatBtnStyles" @click.stop="onShareFb()">Share to Facebook</button>
      </template>
    </div>
  </div>
</template>

<script>
import Badge from '@components/badge.vue'
import { ReferralProgramHistoryStatus } from '@/shared/enums/referral.enum.js'
import { mapGetters, mapState } from 'vuex'
export default {
  name: 'referral-history-item',
  components: {
    Badge
  },
  props: ['data'],
  filters: {
    statusName: function (statusId, statuses) {
      let status = statuses.find(x => x.id === statusId)
      if (!status) return
      return status.text
    }
  },
  computed: {
    ...mapState({
      customization: (state) => state.common.customization,
      salesPersonName: (state) => state.recommendFriend.mainInfo ? state.recommendFriend.mainInfo.salespersonFirstName : null
    }),
    ...mapGetters({
      statuses: 'recommendFriend/statuses'
    }),
    statusClass () {
      const className = {
        [ReferralProgramHistoryStatus.Added]: 'red',
        [ReferralProgramHistoryStatus.Engaged]: 'orange',
        [ReferralProgramHistoryStatus.Purchased]: 'green',
        [ReferralProgramHistoryStatus.Sent]: 'blue'
      }
      return className[this.data.status] || 'default'
    },
    earnLabelClass () {
      const classMap = {
        [ReferralProgramHistoryStatus.Purchased]: 'green'
      }
      return classMap[this.data.status] || 'default'
    },
    cardClass () {
      const classMap = {
        [ReferralProgramHistoryStatus.Purchased]: 'green'
      }
      return classMap[this.data.status]
    },
    referralName () {
      return `${this.data.firstName} ${this.data.lastName}`
    },
    earnText () {
      return `$${this.data.reward} ${this.data.status === ReferralProgramHistoryStatus.Purchased ? 'Earned' : 'Pending'}`
    },
    flatBtnStyles () {
      return {
        color: this.customization.fontColor,
        'background-color': this.customization.mainColor
      }
    },
    strokedBtnStyles () {
      return {
        color: this.customization.mainColor,
        'border-color': this.customization.mainColor
      }
    }
  },
  methods: {
    onShareFb () {
      this.$emit('onShareFb')
    },
    onEdit () {
      this.$emit('edit')
    },
    onSend () {
      this.$emit('send')
    },
    onResend () {
      this.$emit('resend')
    },
    onSendSalespersonSms () {
      this.$emit('sendSalespersonSms')
    }
  },
  data: function () {
    return {
      ReferralProgramHistoryStatus: ReferralProgramHistoryStatus
    }
  }
}
</script>

<style scoped lang="scss">
.history-card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  p {
    margin: 0;
  }

  &.green {
    border-width: 2px;
    border-color: #1CB995;
  }

  &-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;

    >*{
      max-width: 100%;
    }
  }

  &-title {
    padding: 0;
    font-size: 16px;
    font-weight: 600;
  }

  &-footer {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  &-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    button {
      flex: 1;
    }

    &.stacked {
      flex-direction: column;

      >button {
        width: 100%;
      }
    }
  }

  .badge {
    padding: 6px 8px;
    text-transform: uppercase;
    min-width: auto;
    min-height: auto;
    line-height: 1;

    &.red {
      background-color: #FF3C6A33;
      color: #FF3C6A;
    }

    &.green {
      background-color: #37A61033;
      color: #1CB995;
    }

    &.orange {
      background-color: #FF5C0033;
      color: #FF5C00;
    }

    &.blue {
      background-color: rgba(18, 94, 206, 0.1);
      color: rgba(18, 94, 206, 1);
    }

    &.default {
      background-color: rgba(218, 218, 218, 0.1);
      color: rgb(41, 41, 41);
    }
  }
}

.earn-label {
  padding: 2px 8px;
  border-radius: 6px;
}

.earn-label.green {
  background-color: #1CB995;
  color: #fff;
}

.earn-label.default {
  background-color: rgba(159, 161, 165, 1);
  color: #fff;
}

.info {
  padding: 6px 8px;
  border-left: 3px solid #125ECE;
  background-color: #F8FAFC;
}
</style>



// WEBPACK FOOTER //
// referral-history-item.vue?49dfc772